import React, { useEffect, useState } from 'react';
import { Col, Form, Input, InputNumber, Row, Select, notification } from 'antd';
import { useAddVehicleMutation, useUpdateVehicleMutation } from '../../../app/api/vehicleApiSlice';
import FileUpload from '../../../components/fileUpload/FileUpload';
import { useSelector } from 'react-redux';
import {
  getVehicleMakes,
  getVehicleModels,
  getVehicleGrades,
  getVehicleColors,
  getVehicleTypes,
  getVehicleConditions,
  getVehicleCylinders,
  getFuelTypes,
  getTransmissions,
  getVehicleCategories,
  getVehicleLocations,
} from '../../../app/store/commonSlice';
import Vehicle from '../../../core/models/Vehicle';
import VehicleFile from '../../../core/models/VehicleFile';
import ViewVehiclePhotos from './ViewVehiclePhotos';
import { DateTime } from 'luxon';
import VehicleStatus from '../../../core/enums/VehicleStatus';

interface IProps {
  vehicle: Vehicle | null;
  onSuccessfulSubmit: () => void;
  drawerVisible: boolean;
}

const EditVehicleForm: React.FC<IProps> = ({
  vehicle,
  onSuccessfulSubmit,
  drawerVisible,
}: IProps) => {
  const [addVehicle] = useAddVehicleMutation();
  const [updateVehicle] = useUpdateVehicleMutation();

  const [form] = Form.useForm();

  const [make, setMake] = useState<number>(vehicle?.make ?? 0);
  const [model, setModel] = useState<number>(vehicle?.model ?? 0);
  const [cc, setCC] = useState<number>(vehicle?.cylinder ?? 0);
  const [grade, setGrade] = useState<number>(vehicle?.grade ?? 0);

  const [vehiclePhotos, setVehiclePhotos] = useState<VehicleFile[]>([]);

  const vehicleMakes = useSelector(getVehicleMakes);
  const vehicleModels = useSelector(getVehicleModels);
  const vehicleGrades = useSelector(getVehicleGrades);
  const vehicleColors = useSelector(getVehicleColors);
  const vehicleTypes = useSelector(getVehicleTypes);
  const vehicleConditions = useSelector(getVehicleConditions);
  const vehicleCylinders = useSelector(getVehicleCylinders);
  const vehicleLocations = useSelector(getVehicleLocations);
  const vehicleCategories = useSelector(getVehicleCategories);
  const fuelTypes = useSelector(getFuelTypes);
  const transmissions = useSelector(getTransmissions);

  useEffect(() => {
    if (!drawerVisible) {
      form.resetFields();
    }
  }, [drawerVisible]);

  const handleUpdateVehicle = (data: Vehicle): void => {
    data.images = vehiclePhotos;

    if (vehicle?.id) {
      data.id = vehicle.id;
      updateVehicle(data)
        .unwrap()
        .then((response) => {
          notification.success({
            message: 'Update Vehicle',
            description: 'Vehicle updated successfully',
          });
          onSuccessfulSubmit();
        })
        .catch((error) => {
          console.log(error);
        });
    } else {
      addVehicle(data)
        .unwrap()
        .then((response) => {
          notification.success({
            message: 'Add Vehicle',
            description: 'Vehicle added successfully',
          });
          onSuccessfulSubmit();
        })
        .catch((error) => {
          console.log(error);
        });
    }
  };

  return (
    <Form
      form={form}
      id='updateVehicleForm'
      layout='vertical'
      initialValues={{
        make: vehicle?.make,
        model: vehicle?.model,
        grade: vehicle?.grade,
        yom: vehicle?.yom,
        mileage: vehicle?.mileage,
        color: vehicle?.color,
        drivetrain: vehicle?.drivetrain,
        revenueLicenseExpDate: vehicle?.revenueLicenseExpDate
          ? DateTime.fromISO(vehicle?.revenueLicenseExpDate as any).toFormat('yyyy-MM-dd')
          : undefined,
        expiryInsuranceDate: vehicle?.expiryInsuranceDate
          ? DateTime.fromISO(vehicle?.expiryInsuranceDate as any).toFormat('yyyy-MM-dd')
          : undefined,
        chassisNo: vehicle?.chassisNo,
        engineNumber: vehicle?.engineNumber,
        plateNumber: vehicle?.plateNumber,
        category: vehicle?.category,
        fuelType: vehicle?.fuelType,
        transmissionType: vehicle?.transmissionType,
        cylinder: vehicle?.cylinder,
        noSeats: vehicle?.noSeats,
        noDoors: vehicle?.noDoors,
        type: vehicle?.type,
        condition: vehicle?.condition,
        location: vehicle?.location,
      }}
      onFinish={handleUpdateVehicle}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='plateNumber'
            label='Registration No.'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='Registration No.' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name='chassisNo'
            label='Chassis No.'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='Chassis No.' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='engineNumber'
            label='Engine No.'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='Engine No.' />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='make' label='Make' rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch
              placeholder='Make'
              optionFilterProp='children'
              onChange={(value) => {
                setMake(value as number);
              }}
              filterOption={(input, option) =>
                (option?.label?.toString().toLocaleLowerCase() ?? '').includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label?.toString() ?? '')
                  .toLowerCase()
                  .localeCompare((optionB?.label?.toString() ?? '').toLowerCase())
              }
              options={vehicleMakes?.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            ></Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='model' label='Model' rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch
              placeholder='Model'
              optionFilterProp='children'
              onChange={(value) => {
                setModel(value as number);
              }}
              filterOption={(input, option) =>
                (option?.label?.toString().toLocaleLowerCase() ?? '').includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label?.toString() ?? '')
                  .toLowerCase()
                  .localeCompare((optionB?.label?.toString() ?? '').toLowerCase())
              }
              options={vehicleModels
                ?.filter((model) => model.makeId === make)
                .map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
            ></Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='cylinder' label='CC' rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch
              placeholder='CC'
              optionFilterProp='children'
              onChange={(value) => {
                setCC(value as number);
              }}
              filterOption={(input, option) =>
                (option?.label?.toString().toLocaleLowerCase() ?? '').includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label?.toString() ?? '')
                  .toLowerCase()
                  .localeCompare((optionB?.label?.toString() ?? '').toLowerCase())
              }
              options={vehicleCylinders
                ?.filter((cylinder) => cylinder.modelId === model)
                .map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
            ></Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='grade' label='Grade' rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch
              placeholder='Grade'
              optionFilterProp='children'
              onChange={(value) => {
                setGrade(value as number);
              }}
              filterOption={(input, option) =>
                (option?.label?.toString().toLocaleLowerCase() ?? '').includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label?.toString() ?? '')
                  .toLowerCase()
                  .localeCompare((optionB?.label?.toString() ?? '').toLowerCase())
              }
              options={vehicleGrades
                ?.filter((grade) => grade.cylinderId === cc)
                .map((item) => ({
                  value: item.id,
                  label: item.name,
                }))}
            ></Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name='fuelType'
            label='Fuel Type'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select placeholder='Fuel Type'>
              {fuelTypes?.map((fuelType) => (
                <Select.Option key={fuelType.id} value={fuelType.id} initialValues={true}>
                  {fuelType.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='type'
            label='Type of Body'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select
              showSearch
              placeholder='Type of Body'
              optionFilterProp='children'
              filterOption={(input, option) =>
                (option?.label?.toString().toLocaleLowerCase() ?? '').includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label?.toString() ?? '')
                  .toLowerCase()
                  .localeCompare((optionB?.label?.toString() ?? '').toLowerCase())
              }
              options={vehicleTypes?.map((item) => ({
                value: item.id,
                label: item.type,
              }))}
            ></Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name='yom'
            label='Year of Manufacture (YoM)'
            rules={[{ required: true, message: 'Required' }]}
          >
            <InputNumber
              placeholder='Year of Manufacture'
              min={1900}
              max={DateTime.now().year}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='color' label='Colour' rules={[{ required: true, message: 'Required' }]}>
            <Select
              showSearch
              placeholder='Colour'
              optionFilterProp='children'
              filterOption={(input, option) =>
                (option?.label?.toString().toLocaleLowerCase() ?? '').includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label?.toString() ?? '')
                  .toLowerCase()
                  .localeCompare((optionB?.label?.toString() ?? '').toLowerCase())
              }
              options={vehicleColors?.map((item) => ({
                value: item.id,
                label: item.name,
              }))}
            ></Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name='noSeats'
            label='No of Seats (Excluding driver)'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Input placeholder='No of Seats (Ex driver)' type='number' min={0} max={100} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='condition' label='Condition of Vehicle'>
            <Select
              showSearch
              placeholder='Condition of Vehicle'
              optionFilterProp='children'
              filterOption={(input, option) =>
                (option?.label?.toString().toLocaleLowerCase() ?? '').includes(input.toLowerCase())
              }
              filterSort={(optionA, optionB) =>
                (optionA?.label?.toString() ?? '')
                  .toLowerCase()
                  .localeCompare((optionB?.label?.toString() ?? '').toLowerCase())
              }
              options={vehicleConditions?.map((item) => ({
                value: item.id,
                label: item.condition,
              }))}
            ></Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='mileage' label='Mileage'>
            <InputNumber placeholder='Mileage' type='number' min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='transmissionType' label='Transmission'>
            <Select placeholder='Transmission'>
              {transmissions?.map((transmission) => (
                <Select.Option key={transmission.id} value={transmission.id} initialValues={true}>
                  {transmission.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='noDoors' label='No of Doors'>
            <InputNumber
              placeholder='No of Doors'
              type='number'
              min={0}
              max={10}
              style={{ width: '100%' }}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='drivetrain' label='Drivetrain(2WD/4WD)'>
            <Select placeholder='Vehicle Category'>
              <Select.Option key='2WD' value='2WD' initialValues={true}>
                2WD
              </Select.Option>
              <Select.Option key='4WD' value='4WD'>
                4WD
              </Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='category' label='Vehicle Category'>
            <Select placeholder='Vehicle Category'>
              {vehicleCategories?.map((category) => (
                <Select.Option key={category.id} value={category.id} initialValues={true}>
                  {category.category}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            name='location'
            label='Location'
            rules={[{ required: true, message: 'Required' }]}
          >
            <Select placeholder='Location'>
              {vehicleLocations?.map((location) => (
                <Select.Option key={location.id} value={location.id} initialValues={true}>
                  {location.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name='revenueLicenseExpDate' label='Expiry Revenue License'>
            <Input placeholder='Expiry Revenue License' type='date' />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name='expiryInsuranceDate' label='Expiry Insurance'>
            <Input placeholder='Expiry Insurance' type='date' />
          </Form.Item>
        </Col>
        <Col span={12}></Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item>
            <ViewVehiclePhotos
              vehicleId={vehicle?.id}
              images={vehicle?.images ?? null}
              delible={vehicle?.status === VehicleStatus.New}
              sortable={false}
            />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item label={`Photos of the Vehicle (Max: ${20 - (vehicle?.images.length ?? 0)})`}>
            <FileUpload
              onChange={(files) => {
                setVehiclePhotos(files);
              }}
              fileType={''}
              reset={drawerVisible}
              maxPhotos={20 - (vehicle?.images.length ?? 0)}
            />
          </Form.Item>
        </Col>
      </Row>
    </Form>
  );
};

export default EditVehicleForm;
